import { Button, Center, Group, Loader, Table, Text, useMantineTheme } from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { routes } from "@/configs/router.config/routes";
import { useUserList } from "@/hooks/useUserList";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { UserService } from "@/services/users/users.service";
import { showError, showSuccess } from "@/utils/notification";

const UserListPage: React.FC = () => {
  const theme = useMantineTheme();
  const { currentWorkspaceId } = useWorkspaces();
  const [opened, setOpened] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const { users, isLoading, mutate } = useUserList();

  const handleOpenDeleteDialog = (id: string) => {
    setTargetId(id);
    setOpened(true);
  };

  const handleDelete = async (id: string) => {
    try {
      setIsDeletingUser(true);
      await UserService.deleteUser(id);
      await mutate();
      showSuccess("ユーザーを削除しました。");
    } catch (error) {
      console.error(error);
      showError("ユーザーの削除に失敗しました。");
    } finally {
      setIsDeletingUser(false);
      setOpened(false);
    }
  };

  const rows = users?.map((user, i) => (
    <Table.Tr key={user.id}>
      <Table.Td>{i + 1}</Table.Td>
      <Table.Td>{user.name}</Table.Td>
      <Table.Td>{user.email}</Table.Td>
      <Table.Td>{user.roleDisplayName()}</Table.Td>
      <Table.Td w={120}>
        <Group gap={10}>
          <Link to={routes.userEdit(currentWorkspaceId)(user.id)} style={{ textDecoration: "none" }}>
            <Group gap={0}>
              <IconPencil size={16} color={theme.colors.blue[5]} />
              <Text c={theme.colors.blue[5]} size="sm">編集</Text>
            </Group>
          </Link>
          <Group gap={0} style={{ cursor: "pointer" }} onClick={() => handleOpenDeleteDialog(user.id)}>
            <IconTrash size={16} color={theme.colors.red[5]} />
            <Text c={theme.colors.red[5]} size="sm">削除</Text>
          </Group>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Group justify="space-between">
        <h1>ユーザー一覧</h1>
        <Link to={routes.userCreate(currentWorkspaceId)}>
          <Button>ユーザーの追加</Button>
        </Link>
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>名前</Table.Th>
            <Table.Th>メールアドレス</Table.Th>
            <Table.Th>権限</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        {!isLoading && <Table.Tbody>{rows}</Table.Tbody>}
      </Table>
      {isLoading && (
        <Center h={100}>
          <Loader color="gray" size="sm" data-testid="loader" />
        </Center>
      )}

      <ConfirmDialog
        opened={opened}
        title="ユーザーの削除"
        message={`「${users?.find((u) => u.id === targetId)?.name ?? ""}」を削除しますか？`}
        confirmColor="red"
        confirmText="削除する"
        onConfirm={() => targetId && handleDelete(targetId)}
        onClose={() => setOpened(false)}
        loading={isDeletingUser}
      />
    </>
  );
};

export default UserListPage;
